import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowDown, faBolt } from '@fortawesome/free-solid-svg-icons'
import {
  getStorefrontConfig,
  listStorefrontCategories,
  listStorefrontProducts,
  listStorefrontTestimonials,
  placeholderImage,
  toExternalImage,
  toExternalImageSrcSet,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { absoluteUrl, brandTitle, buildFaqSchema, categoryOgImage } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import {
  buildFitItems,
  extractTestimonialImages,
  LP_HERO_IMAGE_TRANSFORM,
  LP_WALL_COUNT,
  LpBrandProtectionNotice,
  LpFaqSection,
  LpFinalCtaSection,
  LpFitSection,
  LpFooter,
  LpInPageProductCatalog,
  LpMinimalHeader,
  LpObjectionsSection,
  LpProductCard,
  LpUrgencyStrip,
  LpWhyUsSection,
  Mark,
  TestimonialWall,
  WhatsAppButton,
} from '../components/GrosirLp'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { loadSockCatalog, selectSockRail } from '../lib/sockCatalog'

const SLUG = 'kaos-kaki'
const PATH = '/grosir-kaos-kaki'
const CATEGORY_NAME = 'Kaos Kaki'
const RAIL_PRODUCT_COUNT = 4
const CATALOG_PAGE_SIZE = 8

// The sock catalog is wide (100+ active models), so instead of one generic
// product grid this page walks through every type we wholesale, each backed by
// a live storefront search within the kaos-kaki category. A type whose search
// comes back empty is hidden automatically.
type SockType = {
  key: string
  search: string
  // Extra search terms whose results get merged into the same rail, for types
  // whose product names don't share one keyword (e.g. sekolah + pramuka).
  extraSearches?: string[]
  name: string
  h2: string
  badge: string
  desc: string
  who: string
  link?: { href: string; label: string }
}

const SOCK_TYPES: SockType[] = [
  {
    key: 'kaos-kaki-wanita',
    search: 'wanita',
    name: 'Kaos Kaki Wanita',
    h2: 'Grosir kaos kaki wanita: ankle dan motif',
    badge: 'Ankle dan motif',
    desc: 'Kelompok ini mencakup kaos kaki ankle wanita dengan motif bunga, hati, dan warna pastel dari merek seperti Gelea serta Kumeiwa.',
    who: 'Ditujukan untuk toko fashion wanita dan reseller yang memerlukan beberapa pilihan motif dalam katalog.',
  },
  {
    key: 'kaos-kaki-bola',
    search: 'bola',
    name: 'Kaos Kaki Bola & Sport',
    h2: 'Grosir kaos kaki bola dan sport',
    badge: 'Dewasa & tanggung',
    desc: 'Kaos kaki panjang untuk latihan dan pertandingan tersedia dari TUYAYA serta LUPO, termasuk model strip. Periksa ukuran dewasa atau tanggung per produk.',
    who: 'Ditujukan untuk toko perlengkapan olahraga serta pembeli tim futsal, SSB, atau komunitas bola.',
  },
  {
    key: 'kaos-kaki-anak',
    search: 'anak',
    name: 'Kaos Kaki Anak',
    h2: 'Grosir kaos kaki anak',
    badge: 'Motif karakter',
    desc: 'Pilihan anak mencakup motif karakter, paket 3 in 1, dan warna pastel. Ukuran perlu diperiksa dari label atau size chart setiap produk.',
    who: 'Ditujukan untuk toko pakaian anak, penjual perlengkapan sekolah, dan reseller yang memerlukan pilihan ukuran serta motif anak.',
  },
  {
    key: 'kaos-kaki-sekolah',
    search: 'sekolah',
    extraSearches: ['pramuka'],
    name: 'Kaos Kaki Sekolah',
    h2: 'Grosir kaos kaki sekolah',
    badge: 'Seragam & pramuka',
    desc: 'Seri Back To School tersedia dalam warna hitam dan putih polos. Kategori ini juga mencakup kaos kaki pramuka TUYAYA.',
    who: 'Ditujukan untuk koperasi sekolah, toko seragam, dan penjual perlengkapan sekolah. Kirim target penerimaan jika pesanan disiapkan untuk tahun ajaran baru.',
  },
  {
    key: 'kaos-kaki-bayi',
    search: 'bayi',
    name: 'Kaos Kaki Bayi',
    h2: 'Grosir kaos kaki bayi',
    badge: 'Anti-slip',
    desc: 'Kaos kaki bayi Winteku tersedia dalam model boy dan girl. Periksa ukuran, bahan, dan konstruksi bintik pada telapak per produk.',
    who: 'Ditujukan untuk toko bayi, penjual hampers kelahiran, dan toko perlengkapan ibu serta anak.',
  },
  {
    key: 'kaos-kaki-pria',
    search: 'pria',
    name: 'Kaos Kaki Pria',
    h2: 'Grosir kaos kaki pria',
    badge: 'Harian & kantor',
    desc: 'Pilihan pria mencakup kaos kaki harian serta seri M3001 sampai M3003 dan TOP. Bandingkan panjang, ukuran, bahan, dan isi kemasan.',
    who: 'Ditujukan untuk toko fashion pria, lapak pasar, dan reseller online yang menyusun stok berdasarkan model serta ukuran.',
  },
  {
    key: 'kaos-kaki-muslimah',
    search: 'muslim',
    name: 'Kaos Kaki Muslimah & Jempol',
    h2: 'Grosir kaos kaki muslimah, jempol, dan tawaf',
    badge: 'Perlengkapan ibadah',
    desc: 'Pilihan meliputi kaos kaki muslimah, model jempol, kaos kaki salat, dan kaos kaki tawaf untuk jamaah haji atau umroh.',
    who: 'Ditujukan untuk toko busana muslim, toko perlengkapan ibadah, serta travel yang menyusun kebutuhan jamaah.',
    link: { href: '/perlengkapan-haji-umroh', label: 'Lihat juga perlengkapan haji & umroh lainnya →' },
  },
  {
    key: 'stocking-wanita',
    search: 'stocking',
    name: 'Stocking Wanita',
    h2: 'Grosir stocking wanita',
    badge: 'Cream & hitam',
    desc: 'Tersedia stocking paha, stocking celana, dan model sheleg dalam warna seperti cream serta hitam. Konfirmasikan warna dan ukuran per model.',
    who: 'Ditujukan untuk toko fashion wanita dan pemasok seragam kerja. Ongkir tetap dihitung dari berat kemasan serta kota tujuan.',
  },
  {
    key: 'kaos-kaki-mata-kaki',
    search: 'mata kaki',
    name: 'Kaos Kaki Mata Kaki & Foot Cover',
    h2: 'Grosir kaos kaki mata kaki dan foot cover',
    badge: 'Invisible',
    desc: 'Kelompok ini memuat kaos kaki sebatas mata kaki dan foot cover yang dirancang agar tertutup oleh sepatu tertentu.',
    who: 'Ditujukan untuk toko sepatu dan fashion yang ingin menawarkan kaos kaki sebagai produk pelengkap.',
  },
]

// FAQ mirrors the proven generic LP set, plus a types question that matches
// this page's angle. Backed by the same policies as the other LPs.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Jenis kaos kaki apa saja yang tersedia?',
    a: 'Katalog mencakup kaos kaki wanita, bola dan sport, anak, sekolah dan pramuka, bayi, pria, muslimah dan jempol, stocking, serta foot cover. Buka setiap kelompok untuk melihat produk yang sedang ditampilkan.',
  },
  {
    q: 'Berapa minimum order kaos kaki?',
    a: 'MOQ kaos kaki mengikuti kategori dan model. Kirim produk serta jumlah yang dibutuhkan agar admin dapat menyiapkan quotation.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Bagaimana cara meminta harga grosir kaos kaki?',
    a: 'Harga mengikuti model dan jumlah pesanan. Gunakan quotation terbaru, lalu hitung margin setelah seluruh biaya penjualan.',
  },
  {
    q: 'Apakah jenis, ukuran, dan warna dapat dicampur?',
    a: 'Ketentuan campur varian berbeda per produk. Kirim pembagian jenis, ukuran, dan warna agar admin dapat memeriksanya sebelum quotation dibuat.',
  },
  {
    q: 'Apakah saya dapat meminta sample?',
    a: 'Ketersediaan sampel perlu diperiksa per produk. Total biaya sampel terdiri dari harga satuan kaos kaki dan ongkir ke kota tujuan.',
  },
  {
    q: 'Bagaimana cara pembayarannya?',
    a: 'Pembayaran dilakukan melalui transfer BCA atau Mandiri. Admin memeriksa bukti transfer dan mengonfirmasi pembayaran sebelum pesanan disiapkan.',
  },
  {
    q: 'Pengirimannya berapa lama?',
    a: 'Admin memeriksa waktu penyiapan setelah produk, jumlah, dan pembayaran dikonfirmasi. Estimasi perjalanan mengikuti kota tujuan serta layanan ekspedisi. Nomor resi dikirim setelah paket diserahkan kepada ekspedisi.',
  },
  {
    q: 'Di mana beli grosir kaos kaki terdekat?',
    a: 'Dropify melayani pembelian grosir kaos kaki secara online melalui WhatsApp dengan pengiriman ke seluruh Indonesia, termasuk Bandung, Jakarta, Surabaya, dan Solo. Pesanan dikirim melalui ekspedisi tanpa perlu datang ke lokasi, dan estimasi ongkos kirim dapat diminta sebelum membayar.',
  },
  {
    q: 'Saya baru akan menjual kaos kaki. Informasi apa yang perlu saya kirim?',
    a: 'Kirim target pembeli, jenis kaos kaki, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan produk, MOQ, quotation, serta pengiriman.',
  },
]

// Reuse the assembled category payload and coalesce concurrent cold requests.
const LP_DATA_TTL_MS = 60 * 60 * 1000
const LP_DATA_EMPTY_TTL_MS = 5 * 60 * 1000

type LpData = {
  category: StorefrontCategory | null
  testimonialImages: string[]
  rails: StorefrontProduct[][]
  catalogResponse: Awaited<ReturnType<typeof listStorefrontProducts>>
  newsPosts: import('../lib/dropify').StorefrontBlogPost[]
}

let lpDataInFlight: Promise<LpData> | null = null
let lpDataCache: { expiresAt: number; data: LpData } | null = null

async function buildLpData(): Promise<LpData> {
  const [categories, testimonialsRaw, catalog, newsPosts] = await Promise.all([
    listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
    listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
    loadSockCatalog().catch(() => ({ products: [] as StorefrontProduct[], total: 0 })),
    listDashboardNewsPosts().catch(() => []),
  ])

  return {
    category: categories.find((item) => item.slug === SLUG) ?? null,
    testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
    rails: SOCK_TYPES.map((type) => selectSockRail(catalog.products, [type.search, ...(type.extraSearches ?? [])], RAIL_PRODUCT_COUNT)),
    catalogResponse: {
      items: catalog.products.slice(0, CATALOG_PAGE_SIZE), total: catalog.total,
      page: 1, per_page: CATALOG_PAGE_SIZE, total_pages: Math.max(1, Math.ceil(catalog.total / CATALOG_PAGE_SIZE)),
    },
    newsPosts,
  }
}

async function getLpDataCached(): Promise<LpData> {
  const cached = lpDataCache
  if (cached && cached.expiresAt > Date.now()) return cached.data
  if (!lpDataInFlight) {
    lpDataInFlight = buildLpData().then((data) => {
      const hasProducts = data.catalogResponse.items.length > 0 || data.rails.some((rail) => rail.length > 0)
      lpDataCache = { expiresAt: Date.now() + (hasProducts ? LP_DATA_TTL_MS : LP_DATA_EMPTY_TTL_MS), data }
      return data
    }).finally(() => { lpDataInFlight = null })
  }
  return lpDataInFlight
}

export const Route = createFileRoute('/grosir-kaos-kaki')({
  loader: async () => {
    const [config, data] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      getLpDataCached(),
    ])
    return { config, ...data }
  },
  head: ({ loaderData }) => {
    const title = brandTitle('Grosir Kaos Kaki untuk Toko dan Reseller')
    const description =
      'Katalog grosir kaos kaki wanita, bola, anak, sekolah, bayi, pria, muslimah, stocking, dan foot cover. Konfirmasikan MOQ, varian, stok, dan quotation melalui WhatsApp.'
    const ogImage = categoryOgImage(loaderData?.category)

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        // Ads-only destination; the matching organic page carries this intent.
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(PATH) },
        { property: 'og:image', content: ogImage },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl(PATH) },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: LandingPage,
})

function SockTypeIndex({ sections }: { sections: Array<{ type: SockType }> }) {
  if (sections.length === 0) return null

  return (
    <section id="jenis-kaos-kaki" className="scroll-mt-6 bg-white py-12">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-6 max-w-2xl">
          <h2 className="text-2xl font-black tracking-tight text-slate-900 md:text-3xl">Pilih jenis kaos kaki yang ingin diperiksa</h2>
          <p className="mt-2 text-base text-slate-500">Ada {sections.length} kelompok produk yang dapat dibuka langsung dari daftar ini.</p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {sections.map(({ type }) => (
            <a
              key={type.key}
              href={`#${type.key}`}
              onClick={() => track('lp_browse_catalog', {
                cta_placement: 'type_index',
                lp_category: CATEGORY_NAME,
                destination: `#${type.key}`,
              })}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
            >
              {type.name}
              <FontAwesomeIcon icon={faArrowDown} className="h-3 w-3 text-slate-400" />
            </a>
          ))}
        </div>
      </div>
    </section>
  )
}

function LandingPage() {
  const { config, category, testimonialImages, rails, catalogResponse, newsPosts } = Route.useLoaderData()

  const sections = SOCK_TYPES
    .map((type, index) => ({ type, products: rails[index] ?? [] }))
    .filter((section) => section.products.length > 0)

  const fitItems = buildFitItems(SLUG, CATEGORY_NAME)
  const heroImage = toExternalImage(category?.image_url, LP_HERO_IMAGE_TRANSFORM) || placeholderImage(CATEGORY_NAME, 800, 800)

  const heroMessage = 'Halo Dropify, saya ingin membeli kaos kaki secara grosir. Jenis: ___. Perkiraan jumlah: ___ lusin. Kota tujuan: ___. Mohon periksa model, stok, harga, dan ongkirnya.'
  const softMessage = 'Halo Dropify, saya sedang mencari kaos kaki grosir. Jenis: ___. Mohon kirim pilihan model dan ketentuan minimum ordernya.'
  const ctaMessage = 'Halo Dropify, saya ingin membeli kaos kaki secara grosir. Jenis: ___. Jumlah: ___ lusin. Kota tujuan: ___. Mohon periksa stok, harga, dan ongkirnya.'

  return (
    <div className="min-h-screen bg-white" data-lp-pattern="handuk-derived">
      <LpUrgencyStrip verifiedOnly />
      <LpMinimalHeader message={heroMessage} category={CATEGORY_NAME} />

      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              Untuk reseller, pemilik toko, dan tim pengadaan
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Grosir kaos kaki untuk <Mark>toko dan reseller</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Pilih kaos kaki wanita, bola, anak, sekolah, bayi, pria, muslimah, stocking, atau foot cover. Minimum order 50 lusin. Kirim jenis, jumlah, dan kota tujuan untuk meminta pilihan model serta harga.
            </p>

            <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Harga dan stok</span>
              <span className="text-lg font-black leading-none text-white">Dikonfirmasi melalui quotation</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta harga dan cek stok"
                category={CATEGORY_NAME}
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href="#jenis-kaos-kaki"
                onClick={() => track('lp_browse_catalog', { cta_placement: 'hero_secondary', lp_category: CATEGORY_NAME })}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Lihat jenis kaos kaki
                <FontAwesomeIcon icon={faArrowDown} className="h-3.5 w-3.5" />
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Siapkan jenis atau model, jumlah lusin, dan kota tujuan.</p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
              <img src={heroImage}
                srcSet={toExternalImageSrcSet(category?.image_url, [400, 600, 800].map((width) => ({ width, options: { ...LP_HERO_IMAGE_TRANSFORM, width, height: width } }))) || undefined}
                sizes="(min-width: 1024px) 460px, (min-width: 640px) 600px, calc(100vw - 32px)"
                alt="Pilihan kaos kaki" width="800" height="800" loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-square object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                <FontAwesomeIcon icon={faBolt} className="h-4 w-4 text-slate-900" />
              </span>
              <div className="leading-tight">
                <p className="text-xs font-black uppercase tracking-wide text-slate-900">Beberapa kelompok produk</p>
                <p className="text-[11px] text-slate-500">Bandingkan di katalog</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SockTypeIndex sections={sections} />

      <LpInPageProductCatalog
        categorySlug={SLUG}
        categoryName={CATEGORY_NAME}
        initialProducts={catalogResponse.items}
        initialPage={catalogResponse.page}
        initialTotalPages={catalogResponse.total_pages}
        heading="Pilihan kaos kaki lainnya"
        description="Katalog juga memuat model pria, wanita, anak, sekolah, sport, muslimah, dan foot cover."
      />

      <AdNewsProofSection posts={newsPosts} />
      <LpBrandProtectionNotice />

      {/* Detailed category rails remain available after the main catalog and proof. */}
      {sections.map(({ type, products }, index) => (
        <SockTypeSection key={type.key} type={type} products={products} zebra={index % 2 === 0} />
      ))}

      {/* Who it's for — let the visitor self-identify */}
      <LpFitSection items={fitItems} />

      {/* Why us */}
      <LpWhyUsSection title="Informasi pemesanan grosir kaos kaki" />

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Objection handling — catch the unsure visitor */}
      <LpObjectionsSection softMessage={softMessage} category={CATEGORY_NAME} />

      {/* FAQ */}
      <LpFaqSection categoryName="kaos kaki" faqItems={FAQ_ITEMS} />

      {/* Final CTA */}
      <LpFinalCtaSection title="Minta quotation grosir kaos kaki" message={ctaMessage} category={CATEGORY_NAME} />

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath={PATH} />

      {/* Footer */}
      <LpFooter config={config} category={CATEGORY_NAME} />
    </div>
  )
}

function SockTypeSection({
  type,
  products,
  zebra,
}: {
  type: SockType
  products: StorefrontProduct[]
  zebra: boolean
}) {
  return (
    <section id={type.key} className={`py-14 scroll-mt-6 border-t border-slate-100 ${zebra ? 'bg-slate-50' : 'bg-white'}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-7 max-w-2xl">
          <span className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{type.badge}</span>
          <h2 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-slate-900">{type.h2}</h2>
          <p className="mt-2.5 text-base leading-relaxed text-slate-600">{type.desc}</p>
          <p className="mt-2 text-base leading-relaxed font-semibold text-slate-800">{type.who}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((product) => (
            <LpProductCard
              key={product.id}
              product={product}
              category={type.name}
              showProductDetailLink
            />
          ))}
        </div>
        <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 px-5 py-5 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div>
            <p className="text-lg font-black text-slate-900">Minta daftar model dalam kategori ini</p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              Kirim jenis, ukuran, jumlah, dan kota tujuan agar admin dapat memeriksa model serta stok yang tersedia.
            </p>
            {type.link && (
              <a href={type.link.href} className="mt-2 inline-block text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700">
                {type.link.label}
              </a>
            )}
          </div>
          <WhatsAppButton
            message={`Halo Dropify, saya ingin membeli ${type.name.toLowerCase()} secara grosir. Mohon kirim daftar produk, MOQ, informasi stok, dan cara meminta quotation.`}
            label={`Minta daftar ${type.name}`}
            category={type.name}
            placement="type_catalog"
            className="mt-4 inline-flex w-fit flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-green-800 sm:mt-0"
          />
        </div>
      </div>
    </section>
  )
}
